import React from 'react';
import { CheckCircle, Circle } from 'lucide-react';
import Card, { CardHeader, CardTitle } from '../ui/Card.tsx';
import Button from '../ui/Button.tsx';

interface SetProgress {
  setId: string;
  name: string;
  imageUrl?: string;
  ownedCount: number;
  totalCount: number;
  progress: number;
  isComplete: boolean;
}

interface SetCompletionWidgetProps {
  sets: SetProgress[];
  loading?: boolean;
  onSetClick?: (setId: string) => void;
}

export default function SetCompletionWidget({
  sets,
  loading = false,
  onSetClick
}: SetCompletionWidgetProps) {
  if (loading) {
    return (
      <Card variant="elevated" padding="lg">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-700 rounded w-40 mb-4" />
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i}>
                <div className="flex justify-between mb-2">
                  <div className="h-4 bg-gray-700 rounded w-32" />
                  <div className="h-4 bg-gray-700 rounded w-16" />
                </div>
                <div className="h-2 bg-gray-700 rounded" />
              </div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  // Sort sets: in-progress first, then by progress descending
  const sortedSets = [...sets].sort((a, b) => {
    if (a.isComplete !== b.isComplete) {
      return a.isComplete ? 1 : -1;
    }
    return b.progress - a.progress;
  });

  const completedCount = sets.filter(s => s.isComplete).length;

  return (
    <Card variant="elevated" padding="lg">
      <CardHeader>
        <CardTitle>Set Completion</CardTitle>
        <span className="text-sm text-gray-400">
          {completedCount} completed
        </span>
      </CardHeader>

      {sets.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500">No sets in progress</p>
          <p className="text-sm text-gray-600 mt-1">
            Add cards to start tracking completion
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedSets.slice(0, 5).map(set => (
            <button
              key={set.setId}
              onClick={() => onSetClick?.(set.setId)}
              className="w-full text-left group"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  {set.isComplete ? (
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                  ) : (
                    <Circle className="w-4 h-4 text-gray-600 flex-shrink-0" />
                  )}
                  <span className="text-sm font-medium text-white truncate group-hover:text-blue-400 transition-colors">
                    {set.name}
                  </span>
                </div>
                <span className="text-sm text-gray-400 flex-shrink-0 ml-2">
                  {set.ownedCount}/{set.totalCount}
                </span>
              </div>

              <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    set.isComplete
                      ? 'bg-gradient-to-r from-green-500 to-green-400'
                      : set.progress >= 0.75
                      ? 'bg-gradient-to-r from-blue-500 to-blue-400'
                      : set.progress >= 0.5
                      ? 'bg-gradient-to-r from-yellow-500 to-yellow-400'
                      : 'bg-gradient-to-r from-gray-500 to-gray-400'
                  }`}
                  style={{ width: `${Math.min(set.progress * 100, 100)}%` }}
                />
              </div>

              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-500">
                  {Math.round(set.progress * 100)}% complete
                </span>
                {!set.isComplete && (
                  <span className="text-xs text-gray-500">
                    {set.totalCount - set.ownedCount} cards needed
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {sets.length > 5 && (
        <Button variant="ghost" size="sm" className="w-full mt-4">
          View all {sets.length} sets
        </Button>
      )}
    </Card>
  );
}
